import { Component, ElementRef, ViewChild, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { AsistenteService } from '../services/asistente.service';
import { VozAsistenteService } from '../services/voz-asistente.service';
import { AsistenteMensaje } from '../models/AsistenteMensaje';

// Cantidad de mensajes de historial que se reenvían en cada consulta (recorte del lado
// del cliente; el backend además aplica su propio tope defensivo).
const HISTORY_LIMIT = 8;

@Component({
    selector: 'app-lia-chat-widget',
    templateUrl: './lia-chat-widget.component.html',
    styleUrls: ['./lia-chat-widget.component.css'],
    standalone: false
})
export class LiaChatWidgetComponent {
  private _authService = inject(AuthService);
  private _asistenteService = inject(AsistenteService);
  readonly _vozService = inject(VozAsistenteService);

  @ViewChild('mensajesEl') private mensajesEl?: ElementRef<HTMLDivElement>;

  readonly isLogged = this._authService.isLoggedIn;

  // Si LIA está operativa (OpenAI configurado del lado del backend). Arranca en false
  // para no mostrar el ícono ni un instante de más si todavía no se confirmó — se
  // consulta una sola vez al crear el componente (persiste durante toda la sesión).
  disponible = signal(false);

  constructor() {
    firstValueFrom(this._asistenteService.estado())
      .then((res) => this.disponible.set(!!res.disponible))
      .catch((e) => {
        console.error('No se pudo confirmar la disponibilidad de LIA:', e);
        this.disponible.set(false);
      });
  }

  isOpen = signal(false);
  isLoading = signal(false);
  historial = signal<AsistenteMensaje[]>([]);
  error = signal<string>('');

  // Lectura automática de las respuestas de LIA en voz alta. Apagado por defecto: que
  // el chat se ponga a hablar solo sin haberlo pedido sería invasivo.
  leerRespuestas = signal(false);

  // Texto actual del input (espejo del value real del <input>, vía (input)) — se usa
  // solo para decidir si mostrar el ícono de enviar o el de micrófono, estilo WhatsApp.
  mensajeTexto = signal('');

  // Segundos transcurridos de la grabación en curso, para el contador de "Escuchando…".
  private tiempoGrabando = signal(0);
  private timerGrabando: ReturnType<typeof setInterval> | null = null;
  tiempoGrabandoTexto = computed(() => {
    const s = this.tiempoGrabando();
    const min = Math.floor(s / 60);
    const seg = s % 60;
    return `${min}:${seg.toString().padStart(2, '0')}`;
  });

  // El ícono de la burbuja se anima con SVG nativo (no CSS); si el usuario prefiere
  // menos movimiento, se muestra el destello fijo, sin transformarse en el globo.
  readonly animarIcono = !(typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

  toggle(): void {
    this.isOpen.update(v => !v);
  }

  cerrar(): void {
    this.isOpen.set(false);
  }

  toggleLectura(): void {
    this.leerRespuestas.update(v => !v);
    if (!this.leerRespuestas()) {
      this._vozService.detenerHabla();
    }
  }

  escuchar(texto: string): void {
    this._vozService.hablar(texto);
  }

  async toggleMic(): Promise<void> {
    if (this._vozService.escuchando()) return;
    this.mensajeTexto.set('');
    this.iniciarTimerGrabando();
    const texto = await this._vozService.iniciarEscucha();
    this.detenerTimerGrabando();
    if (texto) {
      this.enviarMensaje(texto);
    }
  }

  cancelarGrabacion(): void {
    this._vozService.cancelarEscucha();
    this.detenerTimerGrabando();
  }

  private iniciarTimerGrabando(): void {
    this.tiempoGrabando.set(0);
    this.timerGrabando = setInterval(() => this.tiempoGrabando.update(v => v + 1), 1000);
  }

  private detenerTimerGrabando(): void {
    if (this.timerGrabando) {
      clearInterval(this.timerGrabando);
      this.timerGrabando = null;
    }
    this.tiempoGrabando.set(0);
  }

  enviarDesde(input: HTMLInputElement | HTMLTextAreaElement): void {
    const texto = input.value.trim();
    if (!texto) return;
    input.value = '';
    this.mensajeTexto.set('');
    this.enviarMensaje(texto);
  }

  private enviarMensaje(texto: string): void {
    if (this.isLoading()) return;
    this.error.set('');

    this.historial.update(h => [...h, { role: 'user', content: texto }]);
    this.scrollAlFinal();
    this.isLoading.set(true);

    const historialParaEnviar = this.historial().slice(-HISTORY_LIMIT);

    firstValueFrom(this._asistenteService.consulta(texto, historialParaEnviar))
      .then((res) => {
        this.historial.update(h => [...h, { role: 'assistant', content: res.respuesta }]);
        if (this.leerRespuestas()) {
          this._vozService.hablar(res.respuesta);
        }
      })
      .catch((e) => {
        console.error('Error en LIA:', e);
        this.historial.update(h => [...h, { role: 'assistant', content: 'LIA no está disponible en este momento, intentá más tarde.' }]);
      })
      .finally(() => {
        this.isLoading.set(false);
        this.scrollAlFinal();
      });
  }

  private scrollAlFinal(): void {
    setTimeout(() => {
      const el = this.mensajesEl?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }
}
